
//import { DateTime } from 'luxon';
import { 
    getFlowObjectApi,
} from '@/js/api-service';
import { getCurrentUserInfo } from '@/js/djangoContextHelpers';
import { FormatDateTimeLocal, HumanizeDateTimeHelper } from '@/js/filters-helpers';

// CLASSES

// TODO - make a store for these, with some sort of cache key / keys for the various things you load and cache in the instance
// then just pull from the store, dispatch things (node stuff, flow transitions, etc.) through the store...
// do that if it really is justified... it can add a lot of complexity and bugs if not done right

const getTimestamp = () => new Date().valueOf();  // current timestamp in milliseconds

class FlowObject {  // a generic FlowObject -- instantiate with model class and ID -- or use static methods for class-level stuff
    constructor({
        id,  // required to instantiate -- otherwise use static methods
        model_class,  // name of backend model class - such as 'QualityIssue'
        flowSubtype = null,  // flow subtype slug, IF USED - even if we use flowSubtype, as long as we don't use list endpoints, we don't need it... ????
        // TODO - need to add customFieldsSubtype also, if flowSubtype and custom fields subtype are ever different or not clearly related by backend
        // add more standard args here
        // pass in something about current user?  or do that in specific methods only?  to know if they are an assigned TU, etc.
        data,  // if instantiating directly, you must always pass in a data object here, otherwise use FlowObject.fromApi({id, model_class})
        // pass in the Vue component this is attached to?  might get messy....should make this a mixin or Vue component itself
    }) {
        // set all things from args object here with this.arg = arg;
        this.id = id;
        this.model_class = model_class;
        this.flowSubtype = flowSubtype;  // slug of the flow subtype, if used
        this.api = getFlowObjectApi(model_class, flowSubtype);
        // DATA
        // should we nest all other data in .data?  or just set them as properties on the object?
        this.data = data;
        this.loadingData = false;
        this.isDataLoaded = false;
        this.dataLastLoadedAt = getTimestamp();  // timestamp of when data was last fetched so we can decide if we need to refresh it or not
        // TRANSITIONS
        this.isTransitionsLoaded = false;
        this.loadingTransitions = false;
        this.callingTransition = false;
        this.activeTransition = null;  // the transition we are currently calling
        this.transitions = [];
        this.transitionsLastLoadedAt = getTimestamp();  // timestamp of when transitions were last fetched so we can decide if we need to refresh them or not
        // NODES
        this.isNodesLoaded = false;
        this.loadingNodes = false;
        this.nodes = [];  // ALL nodes
        this.nodesLastLoadedAt = getTimestamp();  // timestamp of when nodes were last fetched so we can decide if we need to refresh them or not
        // setup a store for node detail????  or just fetch it when needed?
        // FILES
        this.isFilesLoaded = false;
        this.loadingFiles = false;
        this.files = [];  // will be nested in fields {field_name, label, files}
        this.filesLastLoadedAt = getTimestamp();  // timestamp of when files were last fetched so we can decide if we need to refresh them or not
        // ROLES / OTUs
        this.teamManagerDataByKey = {};  // store team manager data by key
        this.loadingTeamManagerData = false;
        this.teamManagerDataLastLoadedAt = getTimestamp();  // timestamp of when team manager data was last fetched so we can decide if we need to refresh it or not
        this.isObjectRolesLoaded = false;
        this.loadingObjectRoles = false;
        this.objectRoles = [];
        this.objectRolesLastLoadedAt = getTimestamp();  // timestamp of when object roles were last fetched so we can decide if we need to refresh them or not
        this.isOtusLoaded = false;
        this.loadingOtus = false;
        this.otus = [];
        this.otusLastLoadedAt = getTimestamp();  // timestamp of when OTUs were last fetched so we can decide if we need to refresh them or not

        // stash stuff from currentUserInfo on the instance
        // we use the same syntax as in Vue mixin... $currentUserInfo
        this.$currentUserInfo = getCurrentUserInfo();  // gets it from window.currentUserInfo, set by Django context / json script tag
        this.currentTenantUserId = this.$currentUserInfo?.tenant_user_id || null;  // current tenant user ID, if available
    }
    // GETTERS - they don't work that well with vue so just use them locally
    get hasData() {
        return Object.keys(this.data).length > 0;
    }
    // API METHODS
    async refresh() {
        console.log('from FlowObject.refresh() - will now refresh');
        await this.fetchAll();
    }
    async fetchAll() {
        // call this from the component mounted() hook 
        return await Promise.all([
            this.fetchData(),
            this.fetchTransitions(),
            this.fetchNodes(),
            this.fetchFiles(),
        ]);
    }
    async fetchData() {
        // TODO - remove this generic retrieve endpoint!  (from backend too)
        // but need to make it optional per model class, as some still need it
        this.loadingData = true;
        const response = await this.api.retrieve(this.id);
        this.data = response.data;  // merge or replace?  will this be reactive???
        // Object.entries(response.data).forEach(([k, v]) => {
        //     this[k] = v;
        // });
        this.dataLastLoadedAt = getTimestamp();
        this.isDataLoaded = true;
        this.loadingData = false;
        return this.data;  // return it so you can use it in the caller
    }
    /// TRANSITIONS
    /// ---------------------------------------------------
    async fetchTransitions() {
        this.loadingTransitions = true;
        const response = await this.api.get_possible_transitions(this.id);
        this.transitions = response.data;
        this.transitionsLastLoadedAt = getTimestamp();
        this.isTransitionsLoaded = true;
        this.loadingTransitions = false;
        return this.transitions;  // return it so you can use it in the caller
    }
    async fetchTransitionDetailOld(triggerName) {
        // actually make the API call to get the detail of the transition (form, etc.) return it
        // TODO - how much do you want to do here vs. just return and let caller handle it?
        // should probably make it consistent 
        console.log('starting fetchTransitionDetail in flowObject for triggerName: ', triggerName);
        console.log('inside of fetchTransitionDetail, this is: ', this);
        let transDetailResp = null;
        try {
            transDetailResp = await this.api.get_transition_detail(this.id, triggerName);
            console.log('got transition detail for triggerName: ', triggerName, ' - response below');
            console.log(transDetailResp);
        } catch (error) {
            // you return a 409 if this is no longer available, or multiple transitions available with same trigger name
            // for now we'll just swallow it, log it, and show warning with message content -- if its a 409
            if (error.response?.status == 409) {
                console.error(`Got error while trying to fetch transition detail for trigger name ${triggerName}`);
                console.error(error);
                // const errMsg = error.response?.data || 'This action is no longer available';
                // NO TOAST HERE - this.$toast.warning(errMsg);
                return null;  // TODO - what to return here?
            } else {
                //this.apiErrorHandler(error);
                return null;  // TODO - what to return here?
            }
        }
        return transDetailResp?.data ?? null;  // return the transition object if you got it?
    }
    async fetchTransitionDetail(triggerName) {
        // fetch the detail from backend.... this should stay simple, let caller handle errors, setting any activeTransition, etc.
        // we may call this from a component, or from another method in this class
        // as of July 4 2025, a 409 status code could be returned, meaninng its not available (but user can see this flow object)
        return await this.api.get_transition_detail(this.id, triggerName);
    }
    async startTransition(triggerName) {
        // TODO - this should really just be in a component, so we can show the modal
        // NOTE: you should likely just be using the FlowObjectTransitionForm component directly, and not calling this method
        console.log('from FlowObject.startTransition() - will now start transition with triggerName: ', triggerName);
        // check if something is already in progress
        // TODO - maybe don't actually set activeTransition until we are ready to actually make the call?
        // calling components may allow for 'loading detail' of multiple transitions in parallel, so we should not block that here
        // let the component decide how to handle this, or if it should block
        //      - just check if this.callingTransition is true or not instead?
        if (this.activeTransition) {
            console.warn(`Tried to startTransition with triggerName ${triggerName} but already have an active transition`);
            return;
        }
        let resp;
        try {
            resp = await this.fetchTransitionDetail(triggerName);
        } catch (error) {
            console.error(`Got error while trying to fetch transition detail for trigger name ${triggerName}`);
            console.error(error);
            // you return a 409 if this is no longer available, or multiple transitions available with same trigger name
            // for now we'll just swallow it, log it, and show warning with message content -- if its a 409
            if (error.response?.status == 409) {
                // TODO (no longer available errors) - swallow it?  re-throw it?  or just return null/undefined?
                // should tell user to refresh the page, likely they have stale data for possible transitions
                console.error('Error status is 409.  No longer available');
                return;
            } else {
                // TODO (other errors) - swallow it?  re-throw it?  or just return null/undefined?
                return;
            }
        }
        if (resp?.data) {
            // if we got a response, then we can set the activeTransition, then return it
            this.activeTransition = resp.data;  // set it here so we know we have it, used in other calls
            return this.activeTransition;
        }
        return;  // anything special here?  or just return undefined?  (that's what we were doing before...)  should be consistent with other early returns...
    }
    clearActiveTransition() {
        // parent can call this too, to clear/cancel something it started
        this.activeTransition = null;
        console.log('from FlowObject.clearActiveTransition() - just cleared active transition');
    }
    async callTransition(triggerName, postData, withRefresh=true) {
        let transObj = null; // set it one of multiple ways below, depends if we have activeTransition set or not
        if (this.activeTransition) {
            // you need activeTransition (fetched from get_transition_detail) if you have a form to submit data
            // if you already have activeTransition set, then make sure we are calling the same one?
            // but we do NOT require activeTransition to be set when calling this, in case you want to call "quiet" transitions, without first calling startTransition
            if (triggerName !== this.activeTransition.trigger) {
                console.error(`Tried to callTransition with triggerName ${transObj.trigger} but already have an active transition with triggerName ${this.activeTransition.trigger} - NOT ALLOWED`);
                return;
            }
            transObj = this.activeTransition;  // clone it?
        } else {
            // this only works if you don't need to show a form to get submit data 
            transObj = this.getTransitionForTrigger(triggerName);  // TODO - we COULD have transitions with same trigger name....
            this.activeTransition = transObj;  // set it here since it wasn't already set
        }
        console.log('from FlowObject.callTransition() - will now call transition with triggerName: ', triggerName, ' and postData: ', postData, 'and withRefresh: ', withRefresh);
        const finalPostData = postData ? { ...postData } : {};
        const triggerData = {
            trigger: triggerName,
            ...finalPostData,
        };
        this.callingTransition = true;
        //this.activeTransition = transObj;
        let resp;  
        // TODO - don't wrap this in try/catch here... just return what we got, let caller handle what to do, and also whether to refresh?
        // this current implementation is an example of this doing too much... only do this if its a component itself (Dec 25 2024)
        try {
            resp = await this.api.call_transition(this.id, triggerData);
        } catch (err) {
            console.log(`got an error calling trigger ${triggerName} on ${this.model_class} ${this.id} - dumping below`);  // so we can see things in RUM tracking
            console.log(err);
            console.log(typeof err);
            console.log(err.toJSON());
            // err.response is where all the good stuff is:  { config, data, headers, request, status, statusText }
            // TODO - you don't have this.$toast here, and don't have this.apiErrorHandler, must pass in something from the component? or return error?
            // if (err.response?.status === 403) {  // if we got 403, either we have someone trying to do bad things, or the state just got updated and that transition is no longer available (this has actually happened)
            //     this.$toast('That action is no longer available.  Try refreshing the page, then try again.');
            // } else {
            //     this.apiErrorHandler(err, 'Error while processing this.  Please contact Supplios support.');
            // }
        }
        this.callingTransition = false;  // although we might want to refresh right away?
        this.clearActiveTransition();
        if (withRefresh) {
            // SHOULD PROBABLY REFRESH EVERYTHING HERE?  or let component call it?
            await this.refresh();
        }
        return resp;  /// should we return { response, error } or just response? or ???
    }
    getPossibleTriggerNames() {
        // make sure transitions are loaded first???
        return this.transitions.map(t => t.trigger);
    }
    getTransitionsForZone(zone) {
        // make sure transitions are loaded first???
        return zone ? this.transitions.filter(trans => (Array.isArray(trans.zones) && trans.zones.includes(zone))) : [];
    }
    getTransitionsByZone() {
        const zones = {};
        this.transitions.forEach(trans => {
            if (Array.isArray(trans.zones)) {
                trans.zones.forEach(zone => {
                    if (!zones[zone]) {
                        zones[zone] = [];
                    }
                    zones[zone].push(trans);
                });
            }
        });
        return zones;
    }
    getTransitionForTrigger(triggerName) {
        // make sure transitions are loaded first???
        const transitionsByTriggerName = Object.fromEntries(this.transitions.map(trans => ([trans.trigger, trans])));
        return transitionsByTriggerName[triggerName];  // could return undefined!
    }
    getTransitions({zone = null, trigger = null}) {
        // get transitions based on zone, trigger, etc.
        // TODO - make sure isTransitionsLoaded is true first??? - see how we did it for objectRoles, otus
        return this.transitions.filter(trans => {
            return (!zone || (Array.isArray(trans.zones) && trans.zones.includes(zone))) && (!trigger || trans.trigger === trigger);
        });
    }
    /// NODES
    /// ---------------------------------------------------
    /// NOTE: you should maybe have a parallel (child) FlowObjectNode class for all the nodes, 
    /// and then this FlowObject class can just manage the nodes
    /// because they have their own internal frontend state also... like having active transitions, etc.
    async fetchNodes() {
        this.loadingNodes = true;
        const response = await this.api.get_visible_nodes(this.id);
        // TODO - transform them to add some special extra frontend things?  or make a FlowObjectNode class similar to this one?
        // BELOW IS WHAT WE DO IN FlowObjectDetail now
        // just do that in caller?  possible... but we cache the nodes here (maybe we should not...) and we also call into this multiple ways, so having it all normalized here would make sense
        const allNodes = [];
        response.data.forEach((n) => {
            // add some extra frontend state to the node, using cameCase to avoid conflicts with backend keys
            const isAssignedTu = (!!this.currentTenantUserId && !!n.assigned_tus && n.assigned_tus.map((atu) => atu.id).includes(this.currentTenantUserId)) || false; 
            n['isAssignedTu'] = isAssignedTu;
            n['showIsAssignedTu'] = n.isAssignedTu && n.is_active;
            const isSupplierTask = !!n.assigned_tus && n.assigned_tus.some((atu) => atu.is_external);
            n['isSupplierTask'] = isSupplierTask;  // this is a supplier task, if it has assigned_tus and at least one of them is external
            n['showIsSupplierTask'] = isSupplierTask && !isAssignedTu && n.is_active; // only show for active tasks?
            n['deadlineFormatted'] = n.deadline ? FormatDateTimeLocal(n.deadline) : '';  // format the deadline date
            n['deadlineTimeUntil'] = n.deadline ? HumanizeDateTimeHelper(n.deadline) : '';  // humanize the deadline date
            allNodes.push(n);
        });
        // this.nodes = response.data;
        this.nodes = allNodes;  // set the nodes to the normalized array
        this.nodesLastLoadedAt = getTimestamp();
        this.isNodesLoaded = true;
        this.loadingNodes = false;
        return this.nodes;  // return it so you can use it in the caller
    }
    async fetchNodeDetail(nodeKey) {
        // get the detail of a specific node, return it -- we don't cache or manage store of node DETAIL internally here 
        return await this.api.get_node_detail(this.id, nodeKey);
    }
    async callNodeTransition(postData, config) {
        // for NODE transitions, we don't maintain any internal state here such as activeNodeTransition... we just provide a way to call the transition and return the response
        // our API payload for node transitions is a bit different than top-level transitions, FYI... hence the differences in signature here
        return await this.api.call_node_transition(this.id, postData, config);
    }
    async fetchNodeChangeAssigneeOptions(nodeKey) {
        // get the options for changing the assignee of a specific node, return it -- and stash locally???
        return await this.api.get_change_assignee_options(this.id, nodeKey);
    }
    // NODE ACCESSOR METHODS
    getNodesForZone(zone) {  // header, summary, task, actions
        return zone ? this.nodes.filter(node => (Array.isArray(node.zones) && node.zones.includes(zone))) : [];
    }
    getNodesByZone() {  // will have one node appear in multiple lists if in multiple zones
        const zones = {};
        this.nodes.forEach(node => {
            if (Array.isArray(node.zones)) {
                node.zones.forEach(zone => {
                    if (!zones[zone]) {
                        zones[zone] = [];
                    }
                    zones[zone].push(node);
                });
            }
        });
        return zones;
    }
    getNodes({zone = null, nodeType = null, assignedTuId = null, onlyActive = false}) {
        // get nodes based on zone, nodeType, or a specific assignedTuId
        // if zone is set, then only get nodes for that zone
        // TODO - make sure isNodesLoaded is true first??? - see how we did it for objectRoles, otus
        return this.nodes.filter(node => {
            return (!onlyActive || !!node.is_active) && (!zone || (Array.isArray(node.zones) && node.zones.includes(zone))) && (!nodeType || node.node_type === nodeType) && (!assignedTuId || Array.isArray(node.assigned_tus) && node.assigned_tus.map(tuObj => tuObj.id).includes(assignedTuId));
        });
    }
    /// FILES
    /// ---------------------------------------------------
    async fetchFiles() {
        this.loadingFiles = true;
        const response = await this.api.get_files(this.id);
        this.files = response.data;
        this.filesLastLoadedAt = getTimestamp();
        this.isFilesLoaded = true;
        this.loadingFiles = false;
    }
    getFilesForField(fieldName) {
        return fieldName ? this.files.filter(file => file.field_name === fieldName) : [];
    }
    /// ROLES / OTUs
    /// ---------------------------------------------------
    // TODO - this fetchTeamManagerData needs more testing as of March 10 2025
    async fetchTeamManagerData(key) {
        // get the team manager config for a specific key, return it -- and stash locally???
        this.loadingTeamManagerData = true;  // by key???
        const response = await this.api.get_team_manager_data(this.id, key);
        const teamManagerData = response.data;
        this.teamManagerDataLastLoadedAt = getTimestamp();
        // merge it in a way that is reactive to Vue 
        this.teamManagerDataByKey = Object.assign({}, this.teamManagerDataByKey, {[key]: teamManagerData});
        this.loadingTeamManagerData = false;
        return teamManagerData;  // return or just set locally?
    }
    async fetchObjectRoles() {
        this.loadingObjectRoles = true;
        const response = await this.api.get_object_roles(this.id);
        this.objectRoles = response.data;
        this.objectRolesLastLoadedAt = getTimestamp();
        this.isObjectRolesLoaded = true;
        this.loadingObjectRoles = false;
        // return them?  or just set them locally?
        return this.objectRoles;
    }
    async getObjectRoles({refresh=false}={}) {
        if (refresh || !this.isObjectRolesLoaded) {
            await this.fetchObjectRoles();
        }
        return this.objectRoles;
    }
    async fetchOtus() {
        this.loadingOtus = true;
        const response = await this.api.get_object_tus(this.id);
        this.otus = response.data;
        this.otusLastLoadedAt = getTimestamp();
        this.isOtusLoaded = true;
        this.loadingOtus = false;
        // return them?  or just set them locally?
        return this.otus;
    }
    async getOtus({refresh=false}={}) {
        if (refresh || !this.isOtusLoaded) {
            await this.fetchOtus();
        }
        return this.otus;
    }
    async getPossibleAddTusForRoleSlug(roleSlug) {
        const resp = await this.api.get_possible_add_tus(this.id, roleSlug);
        const addList = resp.data.map(tu => {
            return {...tu, disabled: tu.unassignable};
        });
        // cache the results locally?  probably not.... risky, but could do it later if needed
        return addList;
    }

    /// WORKING WITH DATA
    /// ---------------------------------------------------
    updateData(newData) {
        const dataToMerge = newData ? newData : {};
        const oldData = this.data ? this.data : {};
        // cloneDeep???
        this.data = Object.assign({}, oldData, dataToMerge);  // how to do this in reactive way such that Vue will see it???
        //this.data = { ...this.data, ...newData };
    }

    // STATIC METHODS
    static async fromApi({id, model_class}) {
        const flowObjectInst = new FlowObject({id, model_class, data: {}});  // instantiate with empty data, then fetch from API immediately
        await flowObjectInst.fetchAll();
        return flowObjectInst;
    }
}




// EXPORTS
export {
    FlowObject,
}